import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { createContext, useContext, useRef } from 'react'
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  Ref,
  RefObject,
  UIEventHandler,
} from 'react'

import { cn } from './cn'
import { FoldChevron } from './FoldChevron'
import { FOCUS_RING, listRow } from './recipes'
import { SelectionMarker } from './SelectionMarker'
import { TABLE_HEAD_CLASS } from './typography'
import { useHorizontalDrag } from '@/hooks/use-horizontal-drag'

export type ColumnAlign = 'left' | 'right' | 'center'

/** How narrow the column that takes the rest may get before the list scrolls
 *  sideways. Wide enough for a name and its truncation. */
const FLEXIBLE_MIN = 'min-w-40'

/**
 * What a column's declared width means for the cell that carries it.
 *
 * One rule, read by the head, the rows, the gutters and the skeleton, or the
 * four lay out to four different grids. A column that shares the free space
 * gets the floor; one with a fixed width holds it, since a flex item shrinks
 * to its content otherwise and eight columns squeezed into what is left
 * beside an open pane show nothing anyone can read.
 */
export const isElasticWidth = (width?: string) =>
  width === undefined || width.includes('flex')

/**
 * What an elastic column declares under `fit` in place of an intrinsic width.
 *
 * A cell that keeps one contributes the whole of its longest unwrapped line to
 * the row's content size — `truncate` clips the line but does not stop it
 * counting — and the row grows to hold it. The free space that creates is then
 * shared out by flex factor, so one long description widens every elastic
 * column beside it, the widest of them most.
 */
const NO_INTRINSIC_WIDTH = 'w-0'

const columnWidth = (width: string | undefined, fitted: boolean) =>
  isElasticWidth(width)
    ? cn(width ?? 'flex-1', fitted && NO_INTRINSIC_WIDTH, FLEXIBLE_MIN)
    : `${width} shrink-0`

/**
 * Whether the columns below give way to the list's width rather than to their
 * content. It belongs to the list rather than to any one column, so `Table`
 * states it and each cell reads it.
 */
const FittedColumns = createContext(false)

const useFittedColumns = () => useContext(FittedColumns)

/** How narrow a drag may leave a column. Below it the label is gone and the
 *  handle is the only thing left to take hold of. */
const MIN_COLUMN_PX = 48

/** What a drag may make of a width it is asking for: whole pixels, and never
 *  narrower than a column anyone can take hold of. */
const columnPx = (width: number) => Math.max(MIN_COLUMN_PX, Math.round(width))

/**
 * The custom property a dragged column's width travels in.
 *
 * Named after the column, and read by every cell of it — the head's, each
 * row's, the skeleton's — so a drag writes one property on the table and every
 * cell follows without React rendering anything.
 *
 * The key goes in as it stands. Folding what CSS cannot parse into something
 * it can would let two columns land on one property and be dragged as one, so
 * a key that is not an identifier is named rather than repaired.
 */
export const columnVar = (key: string) => {
  if (import.meta.env.DEV && !/^[A-Za-z0-9_-]+$/.test(key)) {
    console.warn(
      `Column key "${key}" is not a CSS identifier, so its width cannot be dragged.`,
    )
  }
  return `--col-${key}`
}

/** A column's width as the two channels that carry it: the declared class, and
 *  the property holding what a drag made of it. */
export type ColumnSize = { width?: string; widthVar?: string }

/**
 * How a column's width reaches the cell that carries it.
 *
 * One call, made by the head, the rows and the skeleton, or the three lay out
 * to three different grids — the two channels have to be resolved together or
 * not at all.
 */
const columnBox = (
  { width, widthVar }: ColumnSize,
  fitted: boolean,
): { className: string; style?: CSSProperties } => ({
  className: columnWidth(width, fitted),
  // The declared class stays on the cell and the inline width beats it. The
  // floor travels with it because an elastic column carries `min-w-40`, which
  // a narrower drag would lose against; the two flex factors because a column
  // given a width must neither grow into the free space nor shrink out of it.
  style: widthVar
    ? {
        width: `var(${widthVar})`,
        minWidth: `var(${widthVar})`,
        flexGrow: 0,
        flexShrink: 0,
      }
    : undefined,
})

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const

const ALIGN: Record<ColumnAlign, string> = {
  left: 'text-left justify-start',
  right: 'text-right justify-end',
  center: 'text-center justify-center',
}

/**
 * The Internal list, as rows of flex cells rather than a `<table>`.
 *
 * @single-use every list goes through `DataTable`, which renders `Table`
 *
 * A real table cannot do what these lists need: a row that is a link, a row
 * that nests under another, a row being dragged. The cells still line up
 * because every row reads the same column widths, and the header carries the
 * ARIA roles so assistive tech is told it is a grid.
 *
 * One scroller for both directions, with the head stuck to its top. Below the
 * width the columns need, the list scrolls sideways: a column squeezed past
 * what it can show is a column nobody can read, and a pane opening beside the
 * list is enough to squeeze eight of them.
 */
export function Table({
  children,
  className,
  style,
  fit = false,
  ref,
  onScroll,
}: {
  children: ReactNode
  className?: string
  /** Where the dragged column widths live, as one custom property per column.
   *  Declared here because every cell below reads them. */
  style?: CSSProperties
  /**
   * Lets the flexible column truncate rather than widen the list.
   *
   * For a list that is a navigation — one name and a figure or two beside it —
   * where a column pushed past the edge is a control nobody finds. A list with
   * columns that have to stay readable leaves it off.
   *
   * Not a promise that the list never scrolls sideways: the fixed columns hold
   * their widths and an elastic one keeps `FLEXIBLE_MIN`, so a narrow pane can
   * still be outgrown. It only decides what gives way first.
   */
  fit?: boolean
  /** The scroll container, for revealing a row and for infinite scroll. */
  ref?: Ref<HTMLDivElement>
  onScroll?: UIEventHandler<HTMLDivElement>
}) {
  return (
    <div
      role="table"
      ref={ref}
      onScroll={onScroll}
      style={style}
      className={cn('min-h-0 flex-1 overflow-auto', className)}
    >
      {/* As wide as the widest row needs, so a row's ground and its hover span
          the whole scroll width instead of stopping where the pane ends —
          `w-full` is the pane, and a list that scrolls sideways paints past it.
          `max-content` lets a column grow to what it shows; under `fit` only
          the floors hold, which is `min-content`. */}
      <div
        className={cn('flex w-full flex-col', fit ? 'min-w-min' : 'min-w-max')}
      >
        <FittedColumns.Provider value={fit}>{children}</FittedColumns.Provider>
      </div>
    </div>
  )
}

/**
 * The head row; a table split into sections has one per section.
 *
 * @single-use part of `Table`; a caller renders the table, not its head
 *
 * Stuck to the top of the one scroller, so the column names stay while the
 * rows move under them. Its own ground, because the rows pass behind it.
 */
export function TableHead({
  children,
  heading,
  className,
}: {
  children: ReactNode
  /** Drawn as a band, like a `TableRow` heading: the head of one section of
   *  a table that has several. */
  heading?: boolean
  className?: string
}) {
  return (
    <div
      role="row"
      className={cn(
        listRow({ heading }),
        TABLE_HEAD_CLASS,
        // `group/head` is what the resize grips read: the pointer anywhere in
        // the head reveals every boundary it could take hold of.
        'group/head sticky top-0 z-20',
        !heading && 'bg-card',
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * A column heading. @single-use — part of `TableHead`.
 *
 * Sortable ones are buttons — the arrow says which way, and
 * an unsorted column shows the neutral glyph so the affordance is visible
 * before the first click rather than only after it.
 *
 * The width class sits on the cell and the label on a child of it, because a
 * resize grip may not be inside the button the label is.
 */
export function TableHeadCell({
  children,
  width,
  widthVar,
  align = 'left',
  valign = 'center',
  sort,
  onSort,
  onResize,
}: {
  children: ReactNode
  /** A Tailwind width class; omit for the column that takes the rest. */
  width?: string
  /** The property carrying what a drag made of this column's width, present
   *  only once it has one. */
  widthVar?: string
  align?: ColumnAlign
  /** `top` for a one-line head beside one that carries a second line, so
   *  the titles stand level. */
  valign?: 'center' | 'top'
  sort?: 'asc' | 'desc' | false
  onSort?: () => void
  /**
   * Makes the column's right edge draggable. Reports the width the pointer is
   * asking for; `null` puts the declared width back.
   */
  onResize?: (width: number | null) => void
}) {
  const cell = useRef<HTMLDivElement>(null)

  const content = (
    <>
      <span className="min-w-0 truncate">{children}</span>
      {sort === 'asc' ? (
        <ArrowUp size={11} className="shrink-0" />
      ) : sort === 'desc' ? (
        <ArrowDown size={11} className="shrink-0" />
      ) : onSort ? (
        <ArrowUpDown size={11} className="shrink-0 opacity-40" />
      ) : null}
    </>
  )

  const label = cn(
    // A label that wraps doubles the head's height and pushes it out of line
    // with every other table on the screen. It truncates instead.
    'flex min-w-0 flex-1 flex-row items-center gap-1 overflow-hidden whitespace-nowrap',
    ALIGN[align],
    // The column the list is ordered by, so the arrow is not the only mark.
    sort && 'text-primary',
  )

  const fitted = useFittedColumns()
  const box = columnBox({ width, widthVar }, fitted)

  return (
    <div
      ref={cell}
      role="columnheader"
      aria-sort={sort ? ARIA_SORT[sort] : onSort ? 'none' : undefined}
      style={box.style}
      // Not `overflow-hidden`: that belongs to the label, and the grip hangs
      // into the gap past this cell's right edge.
      className={cn(
        box.className,
        'relative flex flex-row items-center',
        valign === 'top' && 'self-start',
      )}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            label,
            `hover:text-foreground ${FOCUS_RING} rounded-md`,
          )}
        >
          {content}
        </button>
      ) : (
        <span className={label}>{content}</span>
      )}

      {onResize ? (
        <ResizeGrip cellRef={cell} widthVar={widthVar} onResize={onResize} />
      ) : null}
    </div>
  )
}

/**
 * The column's right edge, as something to take hold of.
 *
 * Its own component so the gesture is mounted only where a column may be
 * resized: a table that offers none runs no drag hook at all.
 *
 * The drag writes the width straight into the custom property on the table
 * and tells React twice — once at the start, so the cells begin reading the
 * property, and once on release, so the width outlives the gesture. A commit
 * per pointer move would redraw every row of the list on every frame.
 *
 * @single-use part of `TableHeadCell`
 */
function ResizeGrip({
  cellRef,
  widthVar,
  onResize,
}: {
  cellRef: RefObject<HTMLDivElement | null>
  widthVar?: string
  onResize: (width: number | null) => void
}) {
  // What the last move asked for, which is the width the release records.
  const live = useRef(0)

  const { dragging, start } = useHorizontalDrag({
    onStart: event => {
      const cell = cellRef.current
      const rect = cell?.getBoundingClientRect()
      // The head cell sits inside the scroller `Table` renders, which is where
      // the properties every row's cell reads are declared.
      const table = cell?.closest<HTMLElement>('[role="table"]')
      if (!rect || !table) return null

      // Committed before the first move: until the column has a width of its
      // own, its cells read the declared class and no property at all.
      live.current = columnPx(rect.width)
      onResize(live.current)
      return { x: event.clientX, width: rect.width, table }
    },
    onMove: (clientX, from) => {
      live.current = columnPx(from.width + clientX - from.x)
      if (widthVar) from.table.style.setProperty(widthVar, `${live.current}px`)
    },
    onEnd: () => onResize(live.current),
  })

  return (
    <span
      // Not focusable: the head's tab stops are its sort controls, so an
      // arrow-key resize would exist on some columns and not others — for a
      // width that is gone on the next reload either way.
      aria-hidden
      onPointerDown={start}
      onDoubleClick={() => onResize(null)}
      // A right-aligned label ends on its cell's edge, so the grip stays
      // wholly inside the gap `listRow` leaves between two cells.
      className="group/grip absolute -right-2.5 top-0 flex h-full w-2.5 cursor-col-resize touch-none items-center justify-center"
    >
      <span
        className={cn(
          'h-3 w-px bg-border opacity-0 transition group-hover/head:opacity-100 group-hover/grip:h-full group-hover/grip:bg-primary',
          dragging && 'h-full bg-primary opacity-100',
        )}
      />
    </span>
  )
}

/**
 * What makes a row press like a button.
 *
 * A row is a `div` and not a `<button>`: it carries a menu, a grip and
 * sometimes an input, and a button inside a button is not a thing a browser
 * will render. So the three parts of being pressable are stated here —
 * the pointer, the keyboard, and the focus the keyboard needs to reach it —
 * and every row that opens something spreads the same ones.
 *
 * The key handler ignores an event from a descendant: a menu inside the row
 * answers the space bar itself, and a row that also opened would answer twice.
 */
/** What a press on a row leaves alone: the controls inside it do what they
 *  say, and the row does nothing more. */
const ROW_CONTROLS = 'button, a, input, select, textarea, [role="menu"]'

/** A row handler that fires only when the press is on the row itself, not
 *  on a button, a link or a field inside it. */
export const pressOutsideControls =
  <E extends HTMLElement>(handler: () => void) =>
  (event: ReactMouseEvent<E>) => {
    if ((event.target as HTMLElement).closest(ROW_CONTROLS)) return
    handler()
  }

export function rowActivation<E extends HTMLElement>(
  onClick: ((event: ReactMouseEvent<E>) => void) | undefined,
) {
  if (!onClick) return {}
  return {
    tabIndex: 0,
    onClick,
    onKeyDown: (event: ReactKeyboardEvent<E>) => {
      if (event.target !== event.currentTarget) return
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onClick(event as unknown as ReactMouseEvent<E>)
    },
  }
}

/**
 * One row.
 *
 * A clickable row stays a `div` rather than becoming a `button`: rows carry
 * checkboxes, chevrons and an action menu, and a button may not contain
 * another. Enter and Space are wired by hand so the keyboard keeps the
 * activation a button would have given for free.
 */
export function TableRow({
  children,
  selected,
  onClick,
  hovers,
  heading,
  className,
}: {
  children: ReactNode
  selected?: boolean
  onClick?: (event: ReactMouseEvent<HTMLDivElement>) => void
  /** Lights up under the pointer although the row is not pressed — see
   *  `listRow`. A pressable row does so already. */
  hovers?: boolean
  /** Drawn as a band over the rows under it. */
  heading?: boolean
  className?: string
}) {
  return (
    <div
      role="row"
      // `useRevealSelection` reads this to find out whether a selected row is
      // on screen. Absent rather than `false` when it is not, so the selector
      // matches only what is selected.
      data-selected={selected ? 'true' : undefined}
      {...rowActivation(onClick)}
      className={cn(
        listRow({ selected, interactive: Boolean(onClick), hovers, heading }),
        className,
      )}
    >
      {selected ? <SelectionMarker /> : null}
      {children}
    </div>
  )
}

/**
 * One cell of a row.
 *
 * @single-use part of `Table`; rows are built from `TableRow`
 */
export function TableCell({
  children,
  width,
  widthVar,
  align = 'left',
  className,
}: {
  children: ReactNode
  width?: string
  /** The property carrying what a drag made of this column's width. */
  widthVar?: string
  align?: ColumnAlign
  className?: string
}) {
  const fitted = useFittedColumns()
  const box = columnBox({ width, widthVar }, fitted)

  return (
    <div
      role="cell"
      style={box.style}
      className={cn(
        box.className,
        'flex flex-row items-center gap-2 truncate',
        ALIGN[align],
        className,
      )}
    >
      {children}
    </div>
  )
}

/** The column a `TableFoldRow`'s chevron takes; the head and every row
 *  that does not fold keep a blank cell of it at the same end. */
export const FOLD_COLUMN = 'w-8'

/**
 * A row that folds a body open under itself.
 *
 * @variant-of ListRow — the same fold, but the line is cells of a `Table`,
 * so its name and figures stay in the columns of the rows around it;
 * `ListRow` is a free line for a list without columns.
 *
 * The line is the press target and draws the chevron at its end, so a name
 * in the first cell lines up with the rows around it that do not fold; a
 * press on a control inside the line does what the control says. The body
 * runs the row's full width on the row's own inset, for a sheet or a table
 * that belongs to the line: an invoice's lines and payments, a group's
 * members.
 */
export function TableFoldRow({
  open,
  onToggle,
  children,
  body,
  chevron = 'end',
  className,
}: {
  open: boolean
  onToggle: () => void
  /** The line's cells. */
  children: ReactNode
  /** What the line opens to; rendered only while open. */
  body: ReactNode
  /** Which end of the line the chevron takes. The head and every other row
   *  of the list keep a `w-8` cell at the same end. */
  chevron?: 'start' | 'end'
  className?: string
}) {
  const fold = (
    <TableCell width={FOLD_COLUMN} align={chevron === 'end' ? 'right' : 'left'}>
      <FoldChevron open={open} />
    </TableCell>
  )
  return (
    // An open group is one block: the line and its body share the ground,
    // and a firmer rule closes the body, so where the next line begins is
    // never in question.
    <div role="rowgroup" className={cn('flex flex-col', open && 'bg-ground')}>
      <TableRow onClick={pressOutsideControls(onToggle)} className={className}>
        {chevron === 'start' ? fold : null}
        {children}
        {chevron === 'end' ? fold : null}
      </TableRow>
      {open ? (
        <div
          role="row"
          className="flex flex-col gap-4 border-b border-border px-3.5 py-3"
        >
          {body}
        </div>
      ) : null}
    </div>
  )
}

/** What a skeleton lays out on when the caller names no columns. */
const DEFAULT_SKELETON: ColumnSize[] = [{ width: 'flex-1' }, { width: 'w-20' }]

/**
 * Holds the row height and the column widths while the data loads, so the
 * list does not jump when it arrives.
 *
 * @single-use what `DataTable` shows while its rows load
 */
export function TableSkeleton({
  rows = 6,
  columns = DEFAULT_SKELETON,
}: {
  rows?: number
  /**
   * One per column — the gutters a row reserves included, or every column
   * moves the moment the data lands. Carries the dragged width as well as the
   * declared one, for the same reason.
   */
  columns?: ColumnSize[]
}) {
  const fitted = useFittedColumns()

  return (
    <div aria-hidden>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className={cn(listRow(), 'py-2.5')}>
          {columns.map((size, column) => (
            <div key={column} {...columnBox(size, fitted)}>
              <div className="h-3.5 w-4/5 animate-pulse rounded bg-line-soft" />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
